import React, { useEffect, Fragment, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";
import Breadcrumb from "../../../../components/common/breadcrumb";
import {
  DeleteOpdRefundEntry,
  GetOpdRefundEntries
} from "../../../../api/opd/opdRefundEntryAPI";

const ListOpdRefundEntry = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchOpds = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetOpdRefundEntries(); // Your API should return a list of OPD Refund entries
      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,
          docNo: item.docNo || "",
          type: item.type || "",
          date: item.date ? new Date(item.date).toLocaleDateString() : "",
          opdNo: item?.opdEntryId?.opdNo || "",
          patientName:
            (item?.opdEntryId?.patientId?.patientNamePrefix
              ? item?.opdEntryId?.patientId?.patientNamePrefix + " "
              : "") +
            (item?.opdEntryId?.patientId?.patientName || ""),
          sex: item?.opdEntryId?.patientId?.sex || "",
          age: item?.opdEntryId?.patientId?.age || "",
          mobileNo: item?.opdEntryId?.patientId?.mobileNo || "",
          address: item?.opdEntryId?.patientId?.address || "",
          paymentMode: item.paymentMode || "N/A",
          amount: item.amount || 0,
          debitAccount: item.debitAccount || "N/A",
          remark: item.remark || "N/A"
        }));
        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchOpds();
  }, []);

  useEffect(
    () => {
      const results = data.filter(item =>
        Object.values(item)
          .join(" ")
          .toLowerCase()
          .includes(search.toLowerCase())
      );
      setFilteredData(results);
    },
    [search, data]
  );

  const handleEditClick = id => {
    navigate(`/opd/transaction/opd-refund-entry/edit/${id}`);
  };

  const handleDeleteClick = async id => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteOpdRefundEntry(id);
          if (response.success) {
            showToast("success", response.message);
            const newData = data.filter(item => item.id !== id);
            setData(newData);
            setFilteredData(newData);
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  const columns = [
    { name: "S.No", selector: row => row.index, sortable: true, width: "10%" },
    { name: "OPD No", selector: row => row.opdNo, sortable: true, width: "10%" },
    { name: "Date", selector: row => row.date, sortable: true, width: "10%" },
    { name: "Patient Name", selector: row => row.patientName, width: "10%" },
    { name: "Sex", selector: row => row.sex, width: "10%" },
    { name: "Age", selector: row => row.age, width: "10%" },
    { name: "Mobile No", selector: row => row.mobileNo, width: "10%" },
    { name: "Address", selector: row => row.address, width: "10%" },
    { name: "Payment Mode", selector: row => row.paymentMode, width: "10%" },
    { name: "Amount", selector: row => row.amount, width: "10%" },
    { name: "Debit Account", selector: row => row.debitAccount, width: "10%" },
    { name: "Remark", selector: row => row.remark, width: "10%" },
    {
      name: "Actions",
      cell: row =>
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil-square-o" />
          </button>
          <UncontrolledTooltip placement="top" target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip placement="top" target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "20%"
    }
  ];

  const headers = [
    { label: "S.No", key: "index" },
    { label: "Doc No", key: "docNo" },
    { label: "Type", key: "type" },
    { label: "Date", key: "date" },
    { label: "OPD No", key: "opdNo" },
    { label: "Patient Name", key: "patientName" },
    { label: "Sex", key: "sex" },
    { label: "Age", key: "age" },
    { label: "Mobile No", key: "mobileNo" },
    { label: "Address", key: "address" },
    { label: "Payment Mode", key: "paymentMode" },
    { label: "Amount", key: "amount" },
    { label: "Debit Account", key: "debitAccount" },
    { label: "Remark", key: "remark" },
  ];

  const ExportCSV = ({ data }) =>
    <CSVLink data={data} headers={headers} filename="opd_refund_entries.csv">
      <button className="btn btn-primary mt-1" id="export">
        Export
      </button>
      <UncontrolledTooltip placement="top" target="export">
        Export to CSV
      </UncontrolledTooltip>
    </CSVLink>;

  return (
    <Fragment>
      <Breadcrumb parent="OPD" title="List OPD Refund Entries" />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h5>List OPD Refund Entries</h5>
                  <Link
                    to={`/opd/transaction/opd-refund-entry/add`}
                  >
                    <button className="btn btn-primary" id="create">
                      Create OPD Refund Entry
                    </button>
                    <UncontrolledTooltip placement="top" target="create">
                      Create New OPD Refund Entry
                    </UncontrolledTooltip>
                  </Link>
                </div>

                <div className="row mt-2">
                  <div className="col-sm-6">
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Search by OPD No, Patient Name..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                  </div>
                  <div className="col-sm-6 text-end">
                    <ExportCSV data={data} />
                  </div>
                </div>
              </div>

              <div className="card-body">
                <DataTable
                  columns={columns}
                  data={filteredData}
                  pagination
                  highlightOnHover
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListOpdRefundEntry;
